import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { batch, useDispatch, useSelector } from 'react-redux';
import PropTypes from 'prop-types';

import { Button, Dropdown, FormRow, Label, Switch, TextField, WithTooltip } from 'basic-components';
import { Info, Running, Settings } from 'basic-components/icons';
import Query from 'dashboard-services/api/Query';
import { ReduxService } from 'dashboard-services/services';
import TimeService from 'dashboard-services/services/TimeService';
import { globalActions, loginActions, userAuthQueries } from 'primary-components';

import { basicSettingsActions } from 'actions/settings';
import getApiConfig from 'api/ApiConfig';
import properties from 'resources/constants/properties.json';
import { routing } from 'routing';
import { AuthSingleton, ExcelUtils, GAUtils, LocalStorageUtils } from 'utils';

import HeaderNoSearch from 'primary/HeaderNoSearch';

import { When } from 'react-if';

import './Settings.scss';

const TOOLTIP_STYLES = { width: 150 }
const numberFormatTypes = ExcelUtils.numberFormatTypes
const numberFormatTypesObj = ExcelUtils.numberFormatTypesObj
const timeZones = TimeService.TIME_ZONES.map(t => ReduxService.updateObject(t, { tooltipPosition: "top" }))
const SettingsComponent = ({ isLoggedIn = false }) => {
  const dispatch = useDispatch(),
        definedTime = useSelector(state => state.settingsState.definedTime),
        definedNumberFormat = useSelector(state => state.settingsState.definedNumberFormat),
        defaultExcelSeriesOptimalization = useSelector(state => state.settingsState.excelSeriesOptimalization),
        defaultApiUrl = useMemo(() => AuthSingleton.getInstance().getApiUrl(), []),
        defaultTimezoneObject = useMemo(() => timeZones.find(c => c.value === definedTime), [definedTime]),
        defaultNumberFormatObj = useMemo(() => numberFormatTypes.find(c => c.value === definedNumberFormat), [definedNumberFormat]),
        [apiUrl, setApiUrl] = useState(defaultApiUrl),
        [timezone, setTimezone] = useState(definedTime),
        [numberFormat, setNumberFormat] = useState(definedNumberFormat),
        [excelSeriesOptimalization, setExcelSeriesOptimalization] = useState(defaultExcelSeriesOptimalization),
        getQueryBuilderNoError = useCallback(() =>
          new Query.Builder(dispatch(getApiConfig()))
            .withShouldDisplayError(false)
        , [dispatch]),
        { mutateAsync: logOutCall } = userAuthQueries.useLogOutMutation({ getQueryBuilder: getQueryBuilderNoError }),
        getQueryBuilder = useCallback(() => new Query.Builder(dispatch(getApiConfig())), [dispatch]),
        { mutateAsync: updateSettingsCall } = userAuthQueries.useUpdateCurrentUserSettings({ getQueryBuilder }),
        saveApiUrl = useCallback(({ numberFormatLocal = numberFormat, timezoneLocal = timezone, apiUrlLocal = apiUrl, excelSeriesOptimalizationLocal = excelSeriesOptimalization }) => {
          let promise = Promise.resolve()
          if((definedTime !== timezoneLocal || definedNumberFormat !== numberFormatLocal || defaultExcelSeriesOptimalization !== excelSeriesOptimalizationLocal) && isLoggedIn && (timeZoneRef.current.validateForForm() && numberFormatRef.current.validateForForm())) {
            promise = updateSettingsCall({
                settings: {
                  definedTime: timeZones.find(s => s?.value === timezoneLocal)?.value || TimeService.UTC,
                  definedNumberFormat: numberFormatTypes.find(s => s?.value === numberFormatLocal)?.value || numberFormatTypesObj.GENERAL,
                  excelSeriesOptimalization: excelSeriesOptimalizationLocal
                }
              })
              .then(() => {
                batch(() => {
                  dispatch(globalActions.getMessageStore()).success("Settings have been saved.")
                  dispatch(basicSettingsActions.onChange(timeZones.find(s => s?.value === timezoneLocal)?.value || TimeService.UTC, "definedTime"))
                  dispatch(basicSettingsActions.onChange(numberFormatTypes.find(s => s?.value === numberFormatLocal).value || numberFormatTypesObj.GENERAL, "definedNumberFormat"))
                  dispatch(basicSettingsActions.onChange(excelSeriesOptimalizationLocal, "excelSeriesOptimalization"))
                })
              })
          }
          if(apiUrlLocal !== defaultApiUrl && apiUrlRef.current.validateForForm()) {
            promise.then(() => {
              if(isLoggedIn) {
                dispatch(loginActions.logOut({ logOutCall, withSideEffects: false }))
              }
              LocalStorageUtils.setApiUrl(apiUrlLocal)
              dispatch(globalActions.getMessageStore()).success("API URL has been changed.")
            })
          }
        }, [apiUrl, defaultApiUrl, defaultExcelSeriesOptimalization, definedNumberFormat, definedTime, dispatch, excelSeriesOptimalization, isLoggedIn, logOutCall, numberFormat, timezone, updateSettingsCall]),
        resetValues = useCallback(() => {
          setApiUrl(properties.baseUrl.api)
          setTimezone(TimeService.UTC)
          setNumberFormat(numberFormatTypesObj.GENERAL)
          timeZoneRef.current?.dropdown?.current?.setValue({ label: TimeService.UTC, value: TimeService.UTC })
          numberFormatRef.current?.dropdown?.current?.setValue({ label: numberFormatTypes.find(s => s?.value === numberFormatTypesObj.GENERAL)?.label, value: numberFormatTypesObj.GENERAL })
          dispatch(globalActions.getToastsStore()).warning("Values have been reset to default. Do you want to save them now?", "Values have been reset to default.", {
            withCancel: false,
            confirmText: "Save",
            confirmAction: () => saveApiUrl({ numberFormatLocal: numberFormatTypesObj.GENERAL, timezoneLocal: TimeService.UTC, apiUrlLocal: properties.baseUrl.api })
          })
        }, [dispatch, saveApiUrl]),
        apiUrlRef = useRef(),
        timeZoneRef = useRef(),
        numberFormatRef = useRef()

  useEffect(() => {
    GAUtils.sendEvent({
      category: "open",
      action: "open_settings",
      label: "open"
    })
    GAUtils.sendPageView(routing.SETTINGS.name)
  }, [])

  return (
    <div
        className="ng-office-app__settings"
    >
      <HeaderNoSearch
          icon={(
            <Settings
                color="grey-dark"
                height={20}
                width={20}
            />
          )}
          title="ADD-IN SETTINGS"
      />
      <div className="ng-office-app__settings__body">
        <div className="ng-office-app__settings__body__section">
          <div className="ng-office-app__settings__body__section__title">
            ADD-IN VERSION
          </div>
          <FormRow
              className="ng-office-app__settings__body__section__body"
              isVertical
          >
            <Label
                size="big"
            >
              Version: {properties.version}
            </Label>
          </FormRow>
        </div>
        <div className="ng-office-app__settings__body__section">
          <div className="ng-office-app__settings__body__section__title">
            API CONFIGURATION
          </div>
          <FormRow
              className="ng-office-app__settings__body__section__body"
              isVertical
          >
            <Label id="api-url">API URL</Label>
            <TextField
                defaultValue={apiUrl}
                labelId="api-url"
                name="apiUrl"
                onChange={setApiUrl}
                ref={apiUrlRef}
                required
                variant="border"
            />
          </FormRow>
        </div>
        <When condition={isLoggedIn}>
          {() => (
            <div className="ng-office-app__settings__body__section">
              <div className="ng-office-app__settings__body__section__title">
                USER SETTINGS
              </div>
              <FormRow className="ng-office-app__settings__body__section__body ng-office-app__settings__body__section__body--horizontal">
                <Label 
                    id="column-optimalization"
                    size="big"
                >
                  Auto-hide columns in series results
                </Label>
                <div className="ng-office-app__settings__body__section__body__right">
                  <WithTooltip
                      data-place="left"
                      extraStyle={TOOLTIP_STYLES}
                      text="Automatically hide columns that show the same filtered value."
                  >
                    <div className="ng-office-app__settings__body__section__body__right__info">
                      <Info
                          color="blue-bright"
                          height={16}
                          width={16}
                      />
                    </div>
                  </WithTooltip>
                  <Switch
                      name="excelSeriesOptimalization"
                      noError
                      on={excelSeriesOptimalization}
                      onChange={setExcelSeriesOptimalization}
                  />
                </div>
              </FormRow>
              <FormRow
                  className="ng-office-app__settings__body__section__body"
                  isVertical
              >
                <Label id="time-zone">User time zone</Label>
                <Dropdown
                    defaultValue={defaultTimezoneObject}
                    labelId="time-zone"
                    name="definedTime"
                    onChange={setTimezone}
                    options={timeZones}
                    ref={timeZoneRef}
                    required
                    variant="border"
                />
              </FormRow>
              <FormRow
                  className="ng-office-app__settings__body__section__body"
                  isVertical
              >
                <Label id="excel-data-type">Excel Data Type</Label>
                <Dropdown
                    defaultValue={defaultNumberFormatObj}
                    labelId="excel-data-type"
                    name="numberFormat"
                    onChange={setNumberFormat}
                    options={numberFormatTypes}
                    ref={numberFormatRef}
                    required
                    variant="border"
                />
              </FormRow>
            </div>
          )}
        </When>
      </div>
      <div className="ng-office-app__settings__footer">
        <Button
            onClick={resetValues}
            size="small"
            variant="text"
        >
          <Running
              bgColor="white-base"
              color="blue-bright"
              height={16}
              width={16}
          />
          <strong>
            Reset
          </strong>
        </Button>
        <Button
            disabled={apiUrl === defaultApiUrl && ((definedTime === timezone && definedNumberFormat === numberFormat && excelSeriesOptimalization === defaultExcelSeriesOptimalization) || !isLoggedIn)}
            onClick={saveApiUrl}
        >
          Save
        </Button>
        <div/>
      </div>
    </div>
  )
}

SettingsComponent.propTypes = {
  isLoggedIn: PropTypes.bool
}

export default SettingsComponent;